import React, { useState } from 'react';
import { CreditCard, Smartphone, Wallet, DollarSign, CheckCircle, ArrowLeft, Copy, QrCode } from 'lucide-react';

const PaymentModal = ({ 
  isOpen,
  onClose, 
  paymentMethod, 
  totalAmount,
  onPaymentComplete 
}) => {
  const [paymentStep, setPaymentStep] = useState('processing'); // processing, success
  const [upiId, setUpiId] = useState('');
  const [cardDetails, setCardDetails] = useState({
    number: '',
    name: '',
    expiry: '',
    cvv: ''
  });
  const [selectedWallet, setSelectedWallet] = useState('');
  const [processing, setProcessing] = useState(false);

  if (!isOpen) return null;

  const handleCardNumberChange = (value) => {
    const cleaned = value.replace(/\s/g, '');
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    setCardDetails({ ...cardDetails, number: formatted });
  };

  const handleExpiryChange = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      const formatted = `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
      setCardDetails({ ...cardDetails, expiry: formatted });
    } else {
      setCardDetails({ ...cardDetails, expiry: cleaned });
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      setProcessing(false);
      setPaymentStep('success');
      
      // Complete payment after 2 seconds
      setTimeout(() => {
        onPaymentComplete();
      }, 2000);
    }, 2000);
  };

  // Success Screen
  if (paymentStep === 'success') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center animate-scale-in">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle size={48} className="text-white" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h2>
          <p className="text-gray-600 mb-6">Your order has been confirmed</p>
          
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="text-sm text-gray-600 mb-1">Amount Paid</div>
            <div className="text-3xl font-bold text-green-600">₹{totalAmount}</div>
          </div>
          
          <p className="text-sm text-gray-500">You will receive a confirmation shortly</p>
        </div>
      </div>
    );
  }

  // UPI Payment Screen
  if (paymentMethod === 'upi') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
        <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={24} />
            </button>
            <h2 className="text-xl font-bold">UPI Payment</h2>
            <div className="w-10"></div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl p-6 mb-6 text-white">
            <div className="text-center mb-4">
              <div className="text-sm opacity-90 mb-1">Amount to Pay</div>
              <div className="text-4xl font-bold">₹{totalAmount}</div>
            </div>
            
            <div className="bg-white p-4 rounded-xl">
              <div className="text-center">
                <QrCode size={180} className="mx-auto text-gray-800 mb-3" />
                <p className="text-sm text-gray-600">Scan with any UPI app</p>
              </div>
            </div>
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">OR</span>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter UPI ID
            </label>
            <input
              type="text"
              placeholder="yourname@upi"
              value={upiId}
              onChange={(e) => setUpiId(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-3 gap-3 mb-6">
            {['PhonePe', 'GPay', 'Paytm'].map((app) => (
              <button
                key={app}
                className="p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 transition-all text-center"
              >
                <Smartphone size={24} className="mx-auto mb-2 text-blue-600" />
                <span className="text-xs font-medium">{app}</span>
              </button>
            ))}
          </div>

          <button
            onClick={handlePayment}
            disabled={processing || !upiId}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
              processing || !upiId
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
            }`}
          >
            {processing ? (
              <span className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-2"></div>
                Processing...
              </span>
            ) : (
              `Pay ₹${totalAmount}`
            )}
          </button>
        </div>
      </div>
    );
  }

  // Card Payment Screen
  if (paymentMethod === 'card') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
        <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={24} />
            </button>
            <h2 className="text-xl font-bold">Card Payment</h2>
            <div className="w-10"></div>
          </div>

          {/* Card Preview */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 mb-6 text-white shadow-2xl">
            <div className="flex justify-between items-start mb-8">
              <div className="text-sm opacity-75">Credit/Debit Card</div>
              <CreditCard size={32} className="opacity-75" />
            </div>
            
            <div className="mb-6">
              <div className="text-2xl font-mono tracking-wider">
                {cardDetails.number || '•••• •••• •••• ••••'}
              </div>
            </div>
            
            <div className="flex justify-between items-end">
              <div>
                <div className="text-xs opacity-75 mb-1">Card Holder</div>
                <div className="font-medium">{cardDetails.name || 'YOUR NAME'}</div>
              </div>
              <div>
                <div className="text-xs opacity-75 mb-1">Expires</div>
                <div className="font-medium">{cardDetails.expiry || 'MM/YY'}</div>
              </div>
            </div>
          </div>

          {/* Card Form */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Card Number
              </label>
              <input
                type="text"
                maxLength="19"
                placeholder="1234 5678 9012 3456"
                value={cardDetails.number}
                onChange={(e) => handleCardNumberChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cardholder Name
              </label>
              <input
                type="text"
                placeholder="John Doe"
                value={cardDetails.name}
                onChange={(e) => setCardDetails({ ...cardDetails, name: e.target.value.toUpperCase() })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiry Date
                </label>
                <input
                  type="text"
                  maxLength="5"
                  placeholder="MM/YY"
                  value={cardDetails.expiry}
                  onChange={(e) => handleExpiryChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CVV
                </label>
                <input
                  type="password"
                  maxLength="3"
                  placeholder="123"
                  value={cardDetails.cvv}
                  onChange={(e) => setCardDetails({ ...cardDetails, cvv: e.target.value.replace(/\D/g, '') })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <button
            onClick={handlePayment}
            disabled={processing || !cardDetails.number || !cardDetails.name || !cardDetails.expiry || !cardDetails.cvv}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
              processing || !cardDetails.number || !cardDetails.name || !cardDetails.expiry || !cardDetails.cvv
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
            }`}
          >
            {processing ? (
              <span className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-2"></div>
                Processing...
              </span>
            ) : (
              `Pay ₹${totalAmount}`
            )}
          </button>
        </div>
      </div>
    );
  }

  // Wallet Payment Screen
  if (paymentMethod === 'wallet') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
        <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={24} />
            </button>
            <h2 className="text-xl font-bold">Wallet Payment</h2>
            <div className="w-10"></div>
          </div>

          <div className="bg-blue-50 rounded-2xl p-6 mb-6 text-center">
            <div className="text-sm text-blue-600 mb-1">Amount to Pay</div>
            <div className="text-4xl font-bold text-blue-600">₹{totalAmount}</div>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mb-4">Select Wallet</h3>

          <div className="space-y-3 mb-6">
            {[
              { id: 'paytm', name: 'Paytm Wallet', balance: '₹1,250', icon: '👛' },
              { id: 'amazonpay', name: 'Amazon Pay', balance: '₹890', icon: '📦' },
              { id: 'phonepe', name: 'PhonePe Wallet', balance: '₹2,100', icon: '💜' },
              { id: 'googlepay', name: 'Google Pay', balance: '₹560', icon: '🔵' }
            ].map((wallet) => (
              <button
                key={wallet.id}
                onClick={() => setSelectedWallet(wallet.id)}
                className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                  selectedWallet === wallet.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{wallet.icon}</span>
                  <div className="text-left">
                    <p className="font-semibold text-gray-800">{wallet.name}</p>
                    <p className="text-sm text-gray-500">Balance: {wallet.balance}</p>
                  </div>
                </div>
                {selectedWallet === wallet.id && (
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <CheckCircle size={16} className="text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>

          <button
            onClick={handlePayment}
            disabled={processing || !selectedWallet}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
              processing || !selectedWallet
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
            }`}
          >
            {processing ? (
              <span className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-2"></div>
                Processing...
              </span>
            ) : (
              `Pay ₹${totalAmount}`
            )}
          </button>
        </div>
      </div>
    );
  }

  // Cash on Delivery Screen
  if (paymentMethod === 'cod') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
        <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={24} />
            </button>
            <h2 className="text-xl font-bold">Cash on Delivery</h2>
            <div className="w-10"></div>
          </div>

          <div className="text-center mb-6">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <DollarSign size={48} className="text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Pay When You Receive</h3>
            <p className="text-gray-600">Keep exact change ready for hassle-free delivery</p>
          </div>

          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <span className="text-2xl">💵</span>
              <div>
                <p className="font-semibold text-yellow-800 mb-1">Amount to be Collected</p>
                <p className="text-3xl font-bold text-yellow-900">₹{totalAmount}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-3">
            <div className="flex items-center gap-3">
              <CheckCircle size={20} className="text-green-600" />
              <span className="text-sm text-gray-700">No online payment required</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle size={20} className="text-green-600" />
              <span className="text-sm text-gray-700">Pay only after receiving your order</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle size={20} className="text-green-600" />
              <span className="text-sm text-gray-700">Safe and secure delivery</span>
            </div>
          </div>

          <button
            onClick={handlePayment}
            disabled={processing}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
              processing
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
            }`}
          >
            {processing ? (
              <span className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-2"></div>
                Confirming...
              </span>
            ) : (
              'Confirm Order'
            )}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default PaymentModal;
